"use client";

import NextImage, { type ImageLoaderProps } from "next/image";
import sbImageLoader from "@/plugins/storyblok/sb-image-loader";
import {
  focusToObjectPosition,
  isStoryblokAsset,
} from "@/plugins/storyblok/sb-image-utils";
import type { FocalPointImageProps } from "./focal-point-image.interface";

/**
 * Drop-in replacement for `next/image` when rendering Storyblok assets.
 *
 * For Storyblok URLs, applies two complementary focal-point mechanisms:
 * - Server-side: Storyblok's image service crops to the focal area at each
 *   requested width via `filters:focal(...)`.
 * - Client-side: `object-position` keeps the focal area in frame when CSS
 *   forces a different aspect ratio than the served crop.
 *
 * Non-Storyblok URLs skip the custom loader and fall through to Next.js's
 * default optimisation pipeline.
 */
export function FocalPointImage({
  src,
  focus,
  alt,
  className,
  style,
  ...rest
}: FocalPointImageProps) {
  const { x, y } = focusToObjectPosition(focus, src);

  const loader = isStoryblokAsset(src)
    ? (props: ImageLoaderProps) => sbImageLoader({ ...props, focus: focus ?? undefined })
    : undefined;

  return (
    <NextImage
      {...rest}
      src={src}
      alt={alt}
      loader={loader}
      className={className}
      style={{
        objectFit: "cover",
        objectPosition: `${x}% ${y}%`,
        ...style,
      }}
      overrideSrc={src}
    />
  );
}
